import { baseURL } from "./constant";

const ACTIVITY_SESSION_KEY = "pm_activity_session_id";
const ACTIVITY_ENDPOINT = `${baseURL}/client-state/`;
const FLUSH_INTERVAL_MS = 5000;
const MAX_BATCH_SIZE = 20;

let queue = [];
let flushTimer = null;

const getClientContext = () => ({
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  language: navigator.language,
  viewport: `${window.innerWidth}x${window.innerHeight}`,
  screen: `${window.screen?.width || ""}x${window.screen?.height || ""}`,
  device_pixel_ratio: window.devicePixelRatio || 1,
});

const getSessionId = () => {
  let sessionId = sessionStorage.getItem(ACTIVITY_SESSION_KEY);
  if (!sessionId) {
    sessionId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
    sessionStorage.setItem(ACTIVITY_SESSION_KEY, sessionId);
  }
  return sessionId;
};

const getProductAreaFromPath = (path = window.location.pathname) => {
  if (path.startsWith("/investing")) return "investing";
  if (path.startsWith("/lnfnwirfyiedozwjrkebvaveuinkbmpajaqaqofmcoxcxuxqryuekrxupdtz")) return "admin";
  if (path === "/login" || path === "/signup" || path === "/custom-token") return "shared";
  return "onboarding";
};

const buildPayload = (events) => {
  const token = localStorage.getItem("token");
  if (!token || events.length === 0) return null;
  return JSON.stringify({
    auth_token: token,
    source: "frontend",
    session_id: getSessionId(),
    events,
  });
};

const sendPayload = (payload, preferBeacon = false) => {
  if (!payload) return false;

  if (preferBeacon && navigator.sendBeacon) {
    try {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(ACTIVITY_ENDPOINT, blob)) {
        return true;
      }
    } catch {
      // Fall through to fetch keepalive.
    }
  }

  fetch(ACTIVITY_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: payload,
    keepalive: true,
  }).catch(() => {});
  return true;
};

export const flushActivity = ({ preferBeacon = false } = {}) => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;

  const batch = queue.splice(0, MAX_BATCH_SIZE);
  sendPayload(buildPayload(batch), preferBeacon);

  if (queue.length > 0) {
    flushActivity({ preferBeacon });
  }
};

const scheduleFlush = () => {
  if (flushTimer || queue.length === 0) return;
  flushTimer = setTimeout(() => flushActivity(), FLUSH_INTERVAL_MS);
};

export const logActivity = (eventType, metadata = {}, options = {}) => {
  if (!eventType || !localStorage.getItem("token")) return;
  const path = `${window.location.pathname}${window.location.search || ""}`;
  const productArea = options.productArea || metadata.product_area || getProductAreaFromPath(path);

  queue.push({
    event_type: eventType,
    occurred_at: new Date().toISOString(),
    path,
    product_area: productArea,
    metadata: {
      ...getClientContext(),
      ...metadata,
      product_area: productArea,
    },
    duration_ms: options.durationMs,
    session_id: getSessionId(),
  });

  if (queue.length >= MAX_BATCH_SIZE || options.flushNow) {
    flushActivity({ preferBeacon: options.preferBeacon });
  } else {
    scheduleFlush();
  }
};

export const installActivityFlushHandlers = () => {
  const flushOnHide = () => {
    if (document.visibilityState === "hidden") {
      flushActivity({ preferBeacon: true });
    }
  };
  const flushOnPageHide = () => flushActivity({ preferBeacon: true });

  document.addEventListener("visibilitychange", flushOnHide);
  window.addEventListener("pagehide", flushOnPageHide);

  return () => {
    document.removeEventListener("visibilitychange", flushOnHide);
    window.removeEventListener("pagehide", flushOnPageHide);
  };
};
